/**
 * Convites e vínculos.
 *
 * **O link é montado aqui, não no servidor.** A API devolve o token; quem sabe
 * o endereço do site é o próprio site. Uma variável de ambiente com a URL
 * pública seria mais uma coisa a errar em produção — e o erro só apareceria
 * depois de o link já ter sido enviado a alguém.
 */

import { api } from '@/lib/apiClient';

export type RelationshipStatus = 'pending' | 'active' | 'paused' | 'ended' | 'rejected';

export interface Relationship {
  relationship_id: string;
  counterpart_id: string;
  /** Nulo quando o vínculo acabou: o acesso ao perfil termina junto com ele. */
  counterpart_name: string | null;
  status: RelationshipStatus;
  started_at: string | null;
  ended_at: string | null;
  /**
   * O apelido que o profissional deu ao convite.
   *
   * É o que identifica um vínculo encerrado, já que o nome deixa de vir. Só o
   * profissional recebe — em `/me/coaches` chega nulo, porque é anotação
   * privada dele sobre outra pessoa.
   */
  invitee_hint: string | null;
}

export interface CreatedInvitation {
  invitation_id: string;
  /** Existe só nesta resposta. Não há rota que o recupere depois. */
  token: string;
  expires_at: string;
  invitee_hint: string | null;
}

export interface OpenInvitation {
  invitation_id: string;
  invitee_hint: string | null;
  expires_at: string;
}

export interface InvitationPreview {
  professional_name: string | null;
  invitee_hint: string | null;
  expires_at: string;
}

export interface StudentDetail {
  relationship_id: string;
  student_id: string;
  display_name: string | null;
  status: RelationshipStatus;
  goals: string[];
  experience_level: string;
  available_minutes: number[];
  locations: string[];
  equipment: string[];
  modalities: string[];
  /** Texto livre sobre saúde. Nunca vai para log, analytics ou compartilhamento. */
  restrictions_text: string | null;
}

export const invitationLink = (token: string) => `${window.location.origin}/convite/${token}`;

export const createInvitation = (invitee_hint: string | null) =>
  api.post<CreatedInvitation>('/professional/invitations', { invitee_hint });

export const listInvitations = () => api.get<OpenInvitation[]>('/professional/invitations');

export const revokeInvitation = (invitationId: string) =>
  api.post<void>(`/professional/invitations/${invitationId}/revoke`);

export const listStudents = () => api.get<Relationship[]>('/professional/students');

export const getStudent = (relationshipId: string) =>
  api.get<StudentDetail>(`/professional/students/${relationshipId}`);

export const listCoaches = () => api.get<Relationship[]>('/me/coaches');

export const changeStatus = (relationshipId: string, status: 'active' | 'paused' | 'ended') =>
  api.post<Relationship>(`/relationships/${relationshipId}/status`, { status });

/** Sem sessão: quem abre o link pode ainda não ter conta. */
export const previewInvitation = (token: string) =>
  api.get<InvitationPreview>(`/public/invitations/${encodeURIComponent(token)}`);

export const acceptInvitation = (token: string) =>
  api.post<Relationship>(`/invitations/${encodeURIComponent(token)}/accept`);

export const declineInvitation = (token: string) =>
  api.post<void>(`/invitations/${encodeURIComponent(token)}/decline`);
